import { useEffect, useState } from 'react';
import { ComparisonComponent } from '../comparison-component';
import { PricingComponent } from '../pricing-component';
import { HeaderNavComponent } from '../header-nav-compontent';
import { MainBackground } from '../main-background';
import { createDemoAccount } from '../../api/thunks/users';
import { extractTrackingIds } from '../../utils/tracking';
import { useAppDispatch } from '../../store/hooks';
import { getLatestEvents, LatestEvent } from '../../api/thunks/financials';
import { formatFiscalDate, getStatusBadge, setCookie } from '../../utils/utils';
import './landing-redesign.css';

const SOURCE_CARDS = [
    ['01', 'Earnings calls', 'What management said — and what it quietly changed.'],
    ['02', 'SEC filings', 'Primary-source facts, before the summaries arrive.'],
    ['03', 'Congressional trades', 'Reported purchases and sales, connected to the policy work around them.'],
    ['04', 'Policy signals', 'Federal action mapped to companies and sectors.'],
    ['05', 'Your watchlist', 'A focused feed instead of another noisy terminal.'],
];

export default function SplashPage9() {
    document.title = 'PocketQuant | Wall Street Intelligence for Main Street';
    const dispatch = useAppDispatch();
    const [isCreatingDemo, setIsCreatingDemo] = useState(false);
    const [latestEvents, setLatestEvents] = useState<LatestEvent[]>([]);
    const [isLoadingEvents, setIsLoadingEvents] = useState(true);

    useEffect(() => {
        getLatestEvents(4)
            .then((response) => setLatestEvents(response.events))
            .catch(() => setLatestEvents([]))
            .finally(() => setIsLoadingEvents(false));
    }, []);

    const handleCreateDemo = () => {
        setIsCreatingDemo(true);
        createDemoAccount(dispatch, JSON.stringify(extractTrackingIds()))
            .then(() => {
                setCookie('demo-account-created', 'true', 1);
                setCookie('user-signed-out', '', -1);
                window.location.href = '/dashboard';
            })
            .catch(() => setIsCreatingDemo(false));
    };

    return (
        <MainBackground>
            <div className="landing-shell">
                <HeaderNavComponent />
                <main>
                    <section className="signal-hero" aria-labelledby="hero-heading">
                        <div className="signal-hero__copy">
                            <p className="eyebrow"><span className="live-dot" /> Live public-market intelligence</p>
                            <h1 id="hero-heading">Know what moved<br /><em>before</em> the market does.</h1>
                            <p className="hero-intro">PocketQuant reads the filings, calls, and policy signals that matter — then turns them into an investor’s next question.</p>
                            <div className="hero-actions">
                                <button type="button" className="primary-action" onClick={handleCreateDemo} disabled={isCreatingDemo}>
                                    {isCreatingDemo ? 'Opening your workspace…' : 'Open the live desk'} <span aria-hidden="true">↗</span>
                                </button>
                                <a className="quiet-action" href="#event-tape">See today’s tape <span aria-hidden="true">↓</span></a>
                            </div>
                            <dl className="hero-proof">
                                <div><dt>92%</dt><dd>FinanceBench<br />accuracy</dd></div>
                                <div><dt>24/7</dt><dd>signal<br />monitoring</dd></div>
                                <div><dt>0</dt><dd>terminal<br />seat fees</dd></div>
                            </dl>
                        </div>

                        <div className="signal-console" aria-label="Example market signal">
                            <div className="console-topline"><span>POCKETQUANT / SIGNAL DESK</span><span>US EQUITIES · LIVE</span></div>
                            <div className="console-company">
                                <div className="ticker-mark">NV</div>
                                <div><strong>NVIDIA</strong><span>NASDAQ: NVDA</span></div>
                                <div className="positive-move"><b>+3.8%</b><span>after-hours</span></div>
                            </div>
                            <div className="console-chart" aria-hidden="true">
                                <span className="chart-label chart-label--high">$184.72</span>
                                <svg viewBox="0 0 640 210" preserveAspectRatio="none"><path d="M0 166 L28 158 L53 162 L85 130 L112 143 L145 124 L181 133 L217 88 L245 109 L280 102 L310 116 L345 70 L381 92 L417 60 L449 74 L481 33 L516 51 L547 24 L580 40 L610 13 L640 20" /></svg>
                                <span className="chart-label chart-label--low">$171.40</span>
                            </div>
                            <div className="signal-card">
                                <div className="signal-card__label"><span className="live-dot" /> New earnings signal</div>
                                <p>Data center revenue accelerated. Guidance implies demand remains supply-constrained.</p>
                                <a href="/try-it">Read the evidence <span aria-hidden="true">→</span></a>
                            </div>
                        </div>
                    </section>

                    <section id="event-tape" className="event-tape" aria-labelledby="event-heading">
                        <div className="section-heading"><p className="eyebrow">The event tape</p><h2 id="event-heading">The market is talking.<br /><em>Listen selectively.</em></h2></div>
                        <p className="section-note">A continuously refreshed cut of the releases, transcripts, and disclosures behind the next move.</p>
                        <div className="event-grid">
                            {isLoadingEvents && Array.from({ length: 4 }).map((_, index) => <div className="event-card event-card--loading" key={index}><i /><i /><i /></div>)}
                            {!isLoadingEvents && latestEvents.map((event) => <EventCard event={event} key={event.id} />)}
                            {!isLoadingEvents && latestEvents.length === 0 && <div className="event-empty">The live feed is reconnecting. <a href="/events">Browse all events →</a></div>}
                        </div>
                        <a className="tape-link" href="/events">Open the complete event tape <span aria-hidden="true">↗</span></a>
                    </section>

                    <section className="source-section" aria-labelledby="sources-heading">
                        <div className="source-intro"><p className="eyebrow">Built from primary sources</p><h2 id="sources-heading">The signal is already<br />in the <em>source material.</em></h2></div>
                        <div className="source-list">
                            {SOURCE_CARDS.map(([number, title, copy]) => <article className="source-row" key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p><span className="source-arrow" aria-hidden="true">↗</span></article>)}
                        </div>
                    </section>

                    <section className="analyst-callout"><p className="eyebrow">The PocketQuant approach</p><p className="analyst-callout__statement">“Great investing isn’t about getting more information. It’s about seeing the <em>right change</em> before everyone else has read it.”</p><a href="/learning-center">How the research desk works <span aria-hidden="true">→</span></a></section>

                    <section className="landing-detail-sections">
                        <ComparisonComponent showNav={false} />
                        <PricingComponent showNav={false} />
                    </section>
                </main>
                <footer className="landing-footer"><span>© PocketQuant</span><div><a href="/terms-of-service">Terms</a><a href="/privacy-policy">Privacy</a><a href="/anti-scraping">Anti-scraping</a></div></footer>
            </div>
        </MainBackground>
    );
}

function EventCard({ event }: { event: LatestEvent }) {
    const badge = getStatusBadge(event.eventType, event.isTranscript);
    return <article className="event-card">
        <div className="event-card__top"><div className="company-avatar"><img src={`/company_icons/${event.ticker}.png`} alt="" onError={(image) => { image.currentTarget.style.display = 'none'; }} /><span>{event.ticker.slice(0, 2)}</span></div><span className={`event-badge ${badge.className}`}>{badge.text}</span></div>
        <h3>{event.companyName}</h3><p className="event-ticker">{event.ticker} · {event.quarter}</p>
        <div className="event-meta"><span>{event.eventType.replace('_', ' ')}</span><time>{formatFiscalDate(event.fiscalDateEnding)}</time></div>
    </article>;
}
